import React, { useState, useEffect } from 'react';
import Loader from '../../components/Loader';
import Navbar from './Navbar';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import formatDateTime from '../../components/formatDateTime';
import { FaArrowLeft, FaArrowRight, FaEye, FaSortAmountDownAlt, FaSortAmountUp, FaTrash } from 'react-icons/fa';
import generateCaptcha from '../../components/captcha';
import { useNavigate } from 'react-router-dom';
import MainButton from '../../components/ul/MainButton';
import ActionButton from '../../components/ul/ActionButton';
import { IoMdSearch } from 'react-icons/io';
import DeleteButton from '../../components/ul/DeleteButton';
import { MdDeleteSweep } from 'react-icons/md';
import { LuDot } from 'react-icons/lu';
import NotFound from '../../components/ul/NotFound';
import getISTDate from '../../components/getISTDate';


const UserAll = () => {
    const [loader, setLoader] = useState(false);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [data, setData] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [sortOrder, setSortOrder] = useState(-1);
    const [selectAll, setSelectAll] = useState(false);
    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate());
    const [totalUser, setTotalUser] = useState(0);
    const [blockUser, setBloackUser] = useState(false);
    const [selectState, setSelectState] = useState('today');
    const limit = 50; // Items per page
    const navigate = useNavigate();

    // Fetch data from API
    const fetchData = async () => {
        setLoader(true);
        try {
            const response = await Calling.getAllUserData(currentPage, startDate, endDate, limit, search, sortOrder, blockUser);
            if (response?.status) {
                setData(response?.data);
                setTotalPages(response?.totalPage);
                setTotalUser(response?.totalRecord);
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoader(false);
        }
    };
    // Fetch data from API
    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [currentPage, sortOrder, startDate, endDate, blockUser]);

    const handleSelectAll = () => {
        if (!selectAll) {
            const allUserIds = data.map((user) => user._id);
            setSelectedUsers(allUserIds);
        } else {
            setSelectedUsers([]);
        }
        setSelectAll(!selectAll);
    };

    const handleSelectUser = (id) => {
        if (selectedUsers.includes(id)) {
            setSelectedUsers(selectedUsers.filter((userId) => userId !== id));
        } else {
            setSelectedUsers([...selectedUsers, id]);
        }
    };

    const handleDateRangeFilter = (range) => {
        const todayUTC = new Date();

        // Convert any UTC date to IST (UTC +5:30) and return YYYY-MM-DD
        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in ms
            return new Date(date.getTime() + istOffset).toISOString().split('T')[0];
        };

        let start, end;

        switch (range) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                setSelectState('today');
                break;

            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                setSelectState('yesterday');
                break;

            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);
                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                setSelectState('thisWeek');
                break;

            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                setSelectState('thisMonth');
                break;

            case 'last6Months':
                const firstDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() - 5, 1);
                const lastDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayLast6UTC);
                end = toIST(lastDayLast6UTC);
                setSelectState('last6Months');
                break;

            case 'thisYear':
                const firstDayOfYearUTC = new Date(todayUTC.getFullYear(), 0, 1);
                const lastDayOfYearUTC = new Date(todayUTC.getFullYear(), 11, 31);
                start = toIST(firstDayOfYearUTC);
                end = toIST(lastDayOfYearUTC);
                setSelectState('thisYear');
                break;

            default:
                start = '';
                end = '';
        }

        setStartDate(start);
        setEndDate(end);
        setCurrentPage(1); // Reset page when filter changes
    };


    const handleDeleteSelected = async (id) => {
        const content = `You are about to delete a user. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (check) {
            try {
                setLoader(true);
                let body = {};
                if (id) {
                    body.id = [id];
                } else {
                    body.id = selectedUsers;
                }
                const response = await Calling.deleteUser(body);
                if (response?.status) {
                    toast.success(response?.message);
                    setSelectedUsers([]);
                    setSelectAll(false); // Reset "Select All"
                    fetchData();
                } else {
                    toast.error(response?.message);
                }
            } catch (error) {
                console.error('Error deleting users:', error);
            } finally {
                setLoader(false);
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    };

    const handleEdit = (id) => {
        navigate(`/manage/user/all/info/${id}`);
    };

    const handleBlockUser = async (id, status) => {
        setLoader(true);
        try {
            const response = await Calling.blockUser(id, status);
            if (response?.status) {
                toast.success(response?.message);
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error('Error blocking user:', error);
        } finally {
            setLoader(false);
            fetchData();
        }
    };

    const handleReportSort = () => {
        setSelectState('sort')
        if (sortOrder === 1) {
            setSortOrder(-1)
        } else {
            setSortOrder(1)
        }
    }

    const handleBlockFiletr = () => {
        setBloackUser(!blockUser)
        setSelectState('block')
    }

    const handleFormDate = (e) => {
        if (e.target.name === "startDate") {
            setStartDate(e.target.value);
            setSelectState('range');
        } else {
            setEndDate(e.target.value);
            setSelectState('range');

        }
    }

    const handleSearchUser = (e) => {
        setCurrentPage(1);
        setSearch(e.target.value)
        setSelectState('search')
    }

    useEffect(() => {
        if (search.trim() !== '') {
            const delayDebounce = setTimeout(() => {
                fetchData();
            }, 500);

            return () => clearTimeout(delayDebounce);
        }
    }, [search]);

    return (
        <>
            <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
                <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">User Management</h1>
                <div className='bg-main-white rounded-xl'>
                    <Navbar selectState={'user'} />
                    <hr className='border-main-border' />

                    {/* Total Users Card */}
                    <div className="p-4 bg-main-th mx-4 my-5 rounded-xl">
                        <h2 className="text-base md:text-lg text-main-subfont">Total Users Created</h2>
                        <p className="text-lg md:text-xl text-main-font">
                            {loader ? (
                                <span className="flex items-center text-main-subfont">
                                    <svg className="animate-spin h-5 w-5 mr-2 text-main-subfont" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Loading...
                                </span>
                            ) : (
                                totalUser < 10 ? '0' + totalUser : totalUser
                            )}
                        </p>
                    </div>

                    <hr className='border-main-border border-[2px]' />

                    {/* Date Range Filters */}
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between w-full space-y-4 lg:space-y-0 lg:space-x-4 px-4 py-3">
                        {/* Buttons Container - Better Responsive Layout */}
                        <div className="flex flex-wrap gap-2 justify-start">
                            <MainButton
                                onClick={() => handleDateRangeFilter('today')}
                                label={'Today'}
                                selectState={selectState === 'today'}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('yesterday')}
                                label={'Yesterday'}
                                selectState={selectState === "yesterday"}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('thisWeek')}
                                label={'This Week'}
                                selectState={selectState === 'thisWeek'}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('thisMonth')}
                                label={'This Month'}
                                selectState={selectState === 'thisMonth'}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('last6Months')}
                                label={'Last 6 Months'}
                                selectState={selectState === 'last6Months'}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('thisYear')}
                                label={'This Year'}
                                selectState={selectState === 'thisYear'}
                            />
                        </div>

                        {/* Date Input Container - Improved Responsive Layout */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 lg:w-auto lg:flex-nowrap lg:whitespace-nowrap">
                            <div className="flex-1 lg:w-auto">
                                <input
                                    type="date"
                                    value={startDate}
                                    name="startDate"
                                    onChange={handleFormDate}
                                    placeholder="Start Date"
                                    className={`border cursor-pointer bg-main-white w-full ${selectState === "range"
                                        ? 'border-main-blue text-main-blue'
                                        : 'border-main-border text-main-subfont'
                                        } px-4 py-2 rounded-lg focus:border-main-blue focus:outline-none shadow-sm`}
                                />
                            </div>
                            <div className="flex-1 lg:w-auto">
                                <input
                                    type="date"
                                    value={endDate}
                                    name="endDate"
                                    onChange={handleFormDate}
                                    placeholder="End Date"
                                    className={`border cursor-pointer bg-main-white w-full ${selectState === "range"
                                        ? 'border-main-blue text-main-blue'
                                        : 'border-main-border text-main-subfont'
                                        } px-4 py-2 rounded-lg focus:border-main-blue focus:outline-none shadow-sm`}
                                />
                            </div>
                        </div>
                    </div>

                    <hr className='bg-main-border/10 mx-4' />

                    {/* Action Buttons and Search */}
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between w-full gap-4 px-4 py-3">
                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2">
                            <MainButton
                                onClick={handleBlockFiletr}
                                selectState={selectState === 'block'}
                                label={blockUser ? 'Unblock' : 'Block'}
                                className='hover:text-main-blue hover:border-main-blue'
                            />
                            <MainButton
                                onClick={handleReportSort}
                                label={
                                    <p className='flex gap-2 items-center'>
                                        {sortOrder === -1 ? <FaSortAmountDownAlt /> : <FaSortAmountUp />}
                                        <span>Sort</span>
                                    </p>
                                }
                                selectState={selectState === 'sort'}
                                className='hover:text-main-blue hover:border-main-blue'
                            />
                        </div>

                        {/* Search Input */}
                        <div className="relative w-full lg:w-64 xl:w-80 mt-2 lg:mt-0">
                            <IoMdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-main-subfont" size={18} />
                            <input
                                type="search"
                                className="p-2 pl-10 border border-main-border rounded-3xl focus:ring-2 focus:ring-main-subfont focus:outline-none focus:border-transparent w-full"
                                value={search}
                                onChange={handleSearchUser}
                                placeholder="Search"
                            />
                        </div>
                    </div>

                    {/* Table of Users - Responsive Overflow */}
                    <div className="overflow-x-auto">
                        <table className='w-full my-5 text-left'>
                            <thead className='bg-main-th text-main-font uppercase'>
                                <tr>
                                    <th className="ps-4 py-3 text-left text-xs font-medium rounded-tl-lg">
                                        <input
                                            type="checkbox"
                                            className='me-2'
                                            checked={selectAll}
                                            onChange={handleSelectAll}
                                        />
                                        {selectedUsers.length > 0 && (
                                            <DeleteButton
                                                onClick={() => handleDeleteSelected(null)}
                                                label={<MdDeleteSweep size={20} />}
                                            />
                                        )}
                                    </th>
                                    <th className="p-2 text-xs font-medium">#</th>
                                    <th className="p-2 text-xs font-medium">profile</th>
                                    <th className="p-2 text-xs font-medium">Name</th>
                                    <th className="p-2 text-xs font-medium">Email</th>
                                    <th className="p-2 text-xs font-medium">Device</th>
                                    <th className="p-2 text-xs font-medium">Credit</th>
                                    <th className="p-2 text-xs font-medium">Status</th>
                                    <th className="p-2 text-xs font-medium">Date</th>
                                    <th className="p-2 text-xs font-medium rounded-tr-lg">Actions</th>
                                </tr>
                            </thead>

                            {loader ? (
                                <tbody>
                                    <tr>
                                        <td colSpan={11} className="text-center py-4">
                                            <Loader loader={loader} />
                                        </td>
                                    </tr>
                                </tbody>
                            ) : (
                                <tbody>
                                    {data.length > 0 ? data.map((user, index) => (
                                        <tr key={user._id} className='hover:bg-main-thHover'>
                                            <td className="ps-4">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUsers.includes(user._id)}
                                                    onChange={() => handleSelectUser(user._id)}
                                                />
                                            </td>
                                            <td className="p-2">{index + 1 + (currentPage - 1) * limit}</td>
                                            <td className="p-2"><img src={user?.u_profile} alt={user.u_name} onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNFNUU3RUIiLz4KPHBhdGggZD0iTTIwIDE4QzIyLjIwOTEgMTggMjQgMTYuMjA5MSAyNCAxNEMyNCAxMS43OTA5IDIyLjIwOTEgMTAgMjAgMTBDMTcuNzkwOSAxMCAxNiAxMS43OTA5IDE2IDE0QzE2IDE2LjIwOTEgMTcuNzkwOSAxOCAyMCAxOFoiIGZpbGw9IiM5Q0EzQUYiLz4KPHBhdGggZD0iTTI4IDI4QzI4IDI0LjY4NjMgMjQuNDE4MyAyMiAyMCAyMkMxNS41ODE3IDIyIDEyIDI0LjY4NjMgMTIgMjhIMjhaIiBmaWxsPSIjOUNBM0FGIi8+Cjwvc3ZnPg==';

                                            }} className='rounded-full w-10 h-10' /></td>
                                            <td className="p-2">{user.u_name}</td>
                                            <td className="p-2">{user.u_email || 'N/A'}</td>
                                            <td className="p-2">{user.u_device || 'N/A'}</td>
                                            <td className="p-2">{user.u_coin || '0'}</td>
                                            <td className="p-2">
                                                <div className="flex space-x-2">
                                                    <span>{user.u_status ?
                                                        <span className='px-3 py-1 w-max text-sm rounded-full bg-green-100 text-green-600 flex items-center'><LuDot size={20} /> Active</span> :
                                                        <span className='px-3 py-1 w-max text-sm rounded-full bg-red-100 text-red-600 flex items-center'><LuDot size={20} /> Blocked</span>
                                                    }</span>
                                                </div>
                                            </td>
                                            <td className="p-2">{formatDateTime(user.createdAt)}</td>
                                            <td className="p-2">
                                                <ActionButtons
                                                    v={user}
                                                    handleDeleteSelected={handleDeleteSelected}
                                                    handleEdit={handleEdit}
                                                    handleBlockUser={handleBlockUser}
                                                />
                                            </td>
                                        </tr>
                                    )) : <tr><td className='' colSpan={11}><NotFound /></td></tr>}
                                </tbody>
                            )}
                        </table>
                    </div>

                    {/* Pagination - Responsive Layout */}
                    <div className="flex flex-col md:flex-row justify-center items-center p-6 relative">
                        <div className="flex gap-6 mb-4 md:mb-0">
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className={`px-2 w-[110px] flex items-center justify-center gap-2 py-2 rounded-lg ${currentPage === 1
                                    ? "bg-main-white border border-main-border text-main-subfont"
                                    : "bg-main-blue text-main-white"
                                    }`}
                            >
                                <FaArrowLeft /> Previous
                            </button>
                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className={`px-2 w-[110px] flex items-center justify-center gap-2 py-2 rounded-lg ${currentPage === totalPages
                                    ? "bg-main-white border border-main-border text-main-subfont"
                                    : "bg-main-blue text-main-white"
                                    }`}
                            >
                                Next <FaArrowRight />
                            </button>
                        </div>

                        <p className="text-main-subfont md:absolute md:right-6 mt-2 md:mt-0">
                            <span className="border border-main-border text-main-font px-2 py-1 rounded-lg">
                                {currentPage <= 10 ? "0" + currentPage : currentPage}
                            </span>{" "}
                            of {totalPages <= 10 ? "0" + totalPages : totalPages}
                        </p>
                    </div>
                </div>
            </div>

        </>
    );
};

export default UserAll;

const ActionButtons = ({ v, handleDeleteSelected, handleEdit, handleBlockUser }) => {
    const [showActions, setShowActions] = useState(false);
    const handleButtonClick = () => {
        setShowActions(!showActions);
    };
    return (
        <div className='relative inline-block '>
            <ActionButton onClick={handleButtonClick} />

            {showActions && (
                <div className='absolute left-0 mt-2 bg-main-white border border-main-border rounded shadow-lg z-10'>
                    {/* <button
                        className='flex items-center px-4 py-2 text-green-600 hover:bg-blue-100 w-full text-left'
                        onClick={() => handleView(v?._id)}
                    >
                        <LiaEyeSolid size={20} className='me-2' /> View
                    </button> */}
                    <div className='flex items-center justify-left ms-4 w-full my-2'>
                        <label className="cursor-pointer mt-1">
                            <input
                                type="checkbox"
                                checked={v?.u_status} // Reflect state in checkbox
                                onChange={(e) => handleBlockUser(v?._id, !v?.u_status)} // Handle checkbox toggle
                                className="sr-only peer"
                                name='user_status'
                            />
                            <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-red-300 dark:peer-focus:ring-red-800 dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-main-white after:border-main-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-red-600"></div>
                        </label>
                    </div>
                    <button
                        className='flex items-center px-4 py-2 text-blue-600 hover:bg-blue-100 w-full text-left'
                        onClick={() => handleEdit(v?._id)}
                    >
                        <FaEye size={20} className='me-2' /> View
                    </button>
                    <button
                        className='flex items-center px-4 py-2 text-red-600 hover:bg-red-100 w-full text-left'
                        onClick={() => handleDeleteSelected(v?._id)}
                    >
                        <FaTrash className='mr-2' /> Delete
                    </button>
                </div>
            )}
        </div>
    )
}
